import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ShieldCheck, Star, TrendingUp, MapPin, Activity, Users, ScanLine, Radio } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable stars (matches VyaparMagicQR styling)
const Stars = ({ score, cls = '' }) => {
  const full = Math.floor(score);
  const half = score - full >= 0.5;
  return (
    <span className={`inline-flex ${cls}`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className={`w-3.5 h-3.5 ${i < full ? 'fill-current' : i === full && half ? 'fill-current opacity-60' : 'opacity-30'}`} />
      ))}
    </span>
  );
};

const TIER_META = {
  elite:    { color: 'text-amber-300',   bg: 'bg-amber-500/10',   bd: 'border-amber-400/40',   emoji: '🏆', label: 'Elite'   },
  trusted:  { color: 'text-blue-300',    bg: 'bg-blue-500/10',    bd: 'border-blue-400/40',    emoji: '💎', label: 'Trusted' },
  verified: { color: 'text-emerald-300', bg: 'bg-emerald-500/10', bd: 'border-emerald-400/40', emoji: '✅', label: 'Verified' },
};

const StatCard = ({ icon: Icon, label, value, color = 'fuchsia', testid }) => (
  <div className={`rounded-2xl border border-${color}-400/30 bg-${color}-500/10 p-4 backdrop-blur-sm`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 text-${color}-300`} />
      <div className={`text-[10px] uppercase tracking-widest font-bold text-${color}-200`}>{label}</div>
    </div>
    <div className="text-3xl font-extrabold text-white">{value}</div>
  </div>
);

export default function NetworkLive() {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/safety-qr/network-live`); setData(r.data); } catch { /* silent */ }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(() => { load(); setTick((v) => v + 1); }, 30000); // 30s refresh
    return () => clearInterval(t);
  }, [load]);
  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-white flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Loading Anant Network live feed…</div>
      </div>
    );
  }
  const { tiers, totals, top_shops = [], recent_alerts = [] } = data;
  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-white" data-testid="anant-live-root">
      {/* Hero */}
      <div className="pt-16 pb-6 px-4 text-center max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-3 py-1 mb-3">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
          <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-200">LIVE · Auto refresh 30s</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-fuchsia-300 via-pink-300 to-amber-300 bg-clip-text text-transparent">
          Anant Network Live Feed
        </h1>
        <p className="text-slate-400 text-sm mt-2 max-w-xl mx-auto">Real-time proof of our growing Trust Network — shops, safety scans, and lives being safeguarded across India.</p>
      </div>

      {/* Top-level totals */}
      <div className="max-w-4xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard icon={Users}     label="Total Shops"    value={totals.shops}       color="fuchsia" testid="stat-shops" />
        <StatCard icon={Shield}    label="Safety QRs"     value={totals.safety_qrs}  color="red"     testid="stat-qrs" />
        <StatCard icon={ScanLine}  label="Today Scans"    value={totals.today_scans} color="cyan"    testid="stat-scans" />
        <StatCard icon={Radio}     label="Total Alerts"   value={totals.total_alerts} color="amber"  testid="stat-alerts" />
      </div>

      {/* Tier distribution */}
      <div className="max-w-4xl mx-auto px-4 mb-4">
        <div className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-2">🏅 Trust Tier Distribution</div>
        <div className="grid grid-cols-3 gap-3" data-testid="tier-distribution">
          {['elite', 'trusted', 'verified'].map((tier) => {
            const m = TIER_META[tier];
            return (
              <div key={tier} className={`rounded-2xl border ${m.bd} ${m.bg} p-4 text-center`} data-testid={`tier-${tier}`}>
                <div className="text-3xl">{m.emoji}</div>
                <div className={`text-2xl font-extrabold ${m.color} mt-1`}>{tiers[tier] || 0}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{m.label} Shops</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top 5 Elite Shops leaderboard */}
      <div className="max-w-4xl mx-auto px-4 mb-4">
        <div className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
          <TrendingUp className="w-3 h-3" /> Top 5 Shops by Trust Score
        </div>
        <div className="space-y-2" data-testid="top-shops-list">
          {top_shops.length === 0 && <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-slate-500 text-xs">No shops yet — be the first!</div>}
          {top_shops.map((s, i) => {
            const m = TIER_META[s.tier] || TIER_META.verified;
            return (
              <div key={i} className={`rounded-xl border ${m.bd} ${m.bg} p-3 flex items-center gap-3`} data-testid={`top-shop-${i}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-extrabold ${i < 3 ? 'bg-amber-500/30 text-amber-200' : 'bg-white/10 text-slate-300'}`}>#{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate flex items-center gap-1.5">
                    {s.business_name}
                    <ShieldCheck className={`w-4 h-4 ${m.color}`} />
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">{s.industry} · {s.customer_count} customers</div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-[10px] uppercase font-bold ${m.color}`}>{m.emoji} {m.label}</div>
                  <div className={`inline-flex items-center gap-1 ${m.color} text-xs mt-0.5`}>
                    <Stars score={s.trust_score || 0} />
                    <span className="font-mono">{(s.trust_score || 0).toFixed(1)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Safety Alerts feed with Maps deep-links */}
      <div className="max-w-4xl mx-auto px-4 mb-4">
        <div className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
          <Activity className="w-3 h-3 text-red-300" /> Recent Safety Activity (Anant Network)
        </div>
        <div className="rounded-2xl border border-red-400/20 bg-slate-900/40 p-3 space-y-1.5" data-testid="recent-alerts">
          {recent_alerts.length === 0 && <div className="text-center text-slate-500 text-xs py-4">No recent alerts — the network is calm 🛡️</div>}
          {recent_alerts.map((a, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg bg-red-500/5 border border-red-400/10 px-2.5 py-1.5" data-testid={`recent-alert-${i}`}>
              <div className="text-[10px] uppercase font-bold text-red-300 bg-red-500/20 rounded px-1.5 py-0.5">{a.asset_type}</div>
              <div className="text-xs text-slate-200 flex-1 truncate">{a.message}</div>
              {a.lat && a.lng && (
                <a href={`https://maps.google.com/?q=${a.lat},${a.lng}`} target="_blank" rel="noreferrer" className="text-blue-300 shrink-0" title="View on map">
                  <MapPin className="w-3.5 h-3.5" />
                </a>
              )}
              <div className="text-[9px] text-slate-500 shrink-0">{new Date(a.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 mt-6">
        <div className="rounded-2xl border border-fuchsia-400/40 bg-gradient-to-r from-fuchsia-500/15 to-pink-500/15 p-4 text-center">
          <div className="text-lg font-bold">Aap bhi Anant Network me join karein 🚀</div>
          <p className="text-xs text-slate-300 mt-1">Apni shop ka Trust Badge earn karein · Safety QR banayein · Affiliate Network webchain me kamai shuru karein.</p>
          <div className="flex gap-2 justify-center mt-3 flex-wrap">
            <Link to="/register" data-testid="live-cta-register" className="px-4 py-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white font-bold text-sm">Free Signup</Link>
            <Link to="/vyapar" data-testid="live-cta-vyapar" className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-sm font-bold">Explore Vyapar</Link>
            <Link to="/safety-qr" data-testid="live-cta-safety" className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-sm font-bold">Safety QR</Link>
          </div>
        </div>
        <div className="text-center text-[10px] text-slate-500 mt-4">Last updated: {new Date(data.generated_at).toLocaleTimeString('en-IN')} · Refresh #{tick + 1}</div>
      </div>
    </div>
  );
}
